import { classifyIdbError } from "./_errors";
import { applySchemaPlan, planSchemaChanges, readExistingStores } from "./_schema";
import type { IdbDatabaseSchema, IdbFailure } from "./_types";

/**
 * 캐시 데이터베이스 연결의 수명.
 *
 * Owns:
 * - 첫 사용까지 미루는 lazy open, 동시 요청의 합류, 업그레이드에서 스키마를 맞추는 일.
 * - 다중 탭 조정. 다른 탭이 업그레이드하면 즉시 연결을 닫는다. 다른 탭이 연결을 유지해서
 *   열지 못하면 기다림을 멈춘다.
 * - 세대. 끝난 수명주기에 늦게 도착한 연결이 다음 수명주기를 덮지 않게 한다.
 *
 * Does not own:
 * - degrade 를 얼마나 오래 유지할지(`_errors` 의 정책과 `public` 의 상태).
 * - 트랜잭션, 읽기, 쓰기(`_bundle`, `public`).
 *
 * 열기 실패를 예외로 던지지 않고 결과 union 으로 돌려준다. 이유는 두 가지다.
 * - 이 계층 전체가 "실패해도 호출부가 try/catch 하지 않는다" 를 지킨다.
 * - 이 계층의 사유(미지원, dispose, 시간 초과)를 가짜 `DOMException` 으로 만들지 않는다.
 */

export type IdbConnectionResult =
  | { ok: true; db: IDBDatabase }
  | { ok: false; failure: IdbFailure };

export type IdbConnectionOptions = {
  schema: IdbDatabaseSchema;
  // 지원하지 않는 환경은 null. 테스트는 여기로 구현을 주입한다.
  factory: IDBFactory | null;
  // 열기와 삭제가 함께 쓰는 시간 예산. 다른 탭이 연결을 유지할 때 기다림을 멈추는 시점이다.
  timeoutMs: number;
};

export class IdbConnection {
  private db: IDBDatabase | null = null;
  private opening: Promise<IdbConnectionResult> | null = null;
  private active = false;
  private generation = 0;

  constructor(private readonly options: IdbConnectionOptions) {}

  /** 활성화한다. 연결은 열지 않는다. 캐시를 쓰지 않는 화면에 비용을 주지 않는다. */
  init(): void {
    if (this.active) {
      return;
    }
    this.active = true;
    this.generation += 1;
  }

  /** **동기여야 한다.** `pagehide` 핸들러가 호출한다. 비동기로 기다리는 작업을 여기에 두지 않는다. */
  dispose(): void {
    this.active = false;
    this.generation += 1;
    this.closeCurrent();
    this.opening = null;
  }

  /** 현재 수명주기의 번호. 이 값이 바뀌었으면 진행 중이던 작업은 버린다. */
  currentGeneration(): number {
    return this.generation;
  }

  async open(): Promise<IdbConnectionResult> {
    if (!this.active) {
      return disposed();
    }
    if (this.db !== null) {
      return { ok: true, db: this.db };
    }
    if (this.opening !== null) {
      return this.opening;
    }
    const opening = this.openDatabase(this.generation);
    this.opening = opening;
    const result = await opening;
    if (this.opening === opening) {
      this.opening = null;
    }
    return result;
  }

  /** 데이터베이스 전체를 지운다. 세션 경계에서 폐기하는 경로이므로 세대 검사를 하지 않는다. */
  async deleteDatabase(): Promise<IdbFailure | null> {
    const factory = this.options.factory;
    if (factory === null) {
      return null;
    }
    // 이 탭의 연결이 열려 있으면 삭제가 blocked 상태로 멈춘다. 그래서 먼저 연결을 닫는다.
    this.closeCurrent();
    this.opening = null;
    try {
      await this.runDelete(factory);
      return null;
    } catch (error) {
      return classifyIdbError(error);
    }
  }

  private runDelete(factory: IDBFactory): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const request = factory.deleteDatabase(this.options.schema.name);
      const settle = this.withTimeout(
        () => reject(timedOut("Deleting the cache database timed out.")),
        () => {
          request.onsuccess = null;
          request.onerror = null;
        },
      );
      request.onsuccess = () => settle(resolve);
      request.onerror = () => settle(() => reject(request.error ?? deleteFailed()));
    });
  }

  private openDatabase(generation: number): Promise<IdbConnectionResult> {
    const factory = this.options.factory;
    if (factory === null) {
      return Promise.resolve(unavailable("IndexedDB is not available in this browser."));
    }
    // `open()` 은 동기로도 예외를 던진다. Safari 의 사생활 보호 모드처럼 저장소가 막힌 환경이
    // 대표적이다. 그 예외를 그대로 전달하면 이 계층의 "거절하지 않는다" 가 깨진다.
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(this.options.schema.name, this.options.schema.version);
    } catch (error) {
      return Promise.resolve({ ok: false, failure: classifyIdbError(error) });
    }
    return new Promise<IdbConnectionResult>((resolve) => {
      const settle = this.withTimeout(
        () =>
          resolve({
            ok: false,
            failure: classifyIdbError(timedOut("Opening the cache database timed out.")),
          }),
        () => {
          // 시간이 초과된 뒤 도착한 연결은 사용할 곳이 없다. 연결을 유지하면 다른 탭의
          // 업그레이드를 막으므로 즉시 닫는다.
          request.onsuccess = () => request.result.close();
          request.onerror = null;
        },
      );
      // `blocked` 는 구독하지 않는다. 다른 탭이 versionchange 핸들러로 연결을 닫으면 자동으로
      // 풀린다. 연결을 닫지 않는 탭은 기다려도 풀리지 않으므로 위 시간 초과가 판정한다.
      request.onupgradeneeded = () => {
        const transaction = request.transaction;
        if (transaction === null) {
          return;
        }
        const existing = readExistingStores(request.result, transaction);
        applySchemaPlan(request.result, planSchemaChanges(existing, this.options.schema.stores));
      };
      request.onsuccess = () =>
        settle(() => {
          const db = request.result;
          if (generation !== this.generation) {
            db.close();
            resolve(disposed());
            return;
          }
          this.adopt(db);
          resolve({ ok: true, db });
        });
      request.onerror = () =>
        settle(() => resolve({ ok: false, failure: classifyIdbError(request.error) }));
    });
  }

  private adopt(db: IDBDatabase): void {
    // 다른 탭이 버전을 올리려 하면 이 탭이 연결을 닫아야 그 탭이 진행한다. 오류가 아니라 정상 동작이다.
    db.onversionchange = () => {
      db.close();
      this.forget(db);
    };
    // 브라우저가 축출이나 디스크 오류로 연결을 강제 종료한 경우. 다음 op 가 다시 연다.
    db.onclose = () => this.forget(db);
    this.db = db;
  }

  private forget(db: IDBDatabase): void {
    if (this.db === db) {
      this.db = null;
    }
  }

  private closeCurrent(): void {
    this.db?.close();
    this.db = null;
  }

  /**
   * 결과를 한 번만 확정하는 경쟁을 만든다. 시간이 초과되면 `onTimeout` 이 결과를 정한다.
   * 늦게 도착한 이벤트는 `afterTimeout` 이 정리한다.
   */
  private withTimeout(
    onTimeout: () => void,
    afterTimeout: () => void,
  ): (finish: () => void) => void {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      afterTimeout();
      onTimeout();
    }, this.options.timeoutMs);
    return (finish) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      finish();
    };
  }
}

function disposed(): IdbConnectionResult {
  return { ok: false, failure: { kind: "aborted", message: "The cache session is closed." } };
}

function unavailable(message: string): IdbConnectionResult {
  return { ok: false, failure: { kind: "unavailable", message } };
}

function timedOut(message: string): DOMException {
  return new DOMException(message, "TimeoutError");
}

function deleteFailed(): DOMException {
  return new DOMException("Deleting the cache database failed.", "UnknownError");
}
